import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import ReactDOM from "react-dom";
import {
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Grid,
  IconButton,
  CircularProgress,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@material-ui/core";
import {
  Close as CloseIcon,
  BarChart as BarChartIcon,
} from "@material-ui/icons";
import CustomChart from "./CustomChart";
import MmsApiService from "../Services/MMS/mms-api.service";
import { useBoxes } from "../Contexts/BoxContext";

const SegmentedProgressBar = ({
  value = 0,
  segments = 30,
  barHeight = 22,
  barRadius = 16,
  borderColor = "#00eaff",
  backgroundColor = "#071a2d",
  segmentWidth = 10.5,
  segmentGap = 1,
}) => {
  const gradientStart = [10, 35, 64];
  const gradientEnd = [0, 234, 255];

  function getSegmentColor(idx) {
    const t = idx / (segments - 1);
    const r = Math.round(
      gradientStart[0] + (gradientEnd[0] - gradientStart[0]) * t
    );
    const g = Math.round(
      gradientStart[1] + (gradientEnd[1] - gradientStart[1]) * t
    );
    const b = Math.round(
      gradientStart[2] + (gradientEnd[2] - gradientStart[2]) * t
    );
    return `rgb(${r},${g},${b})`;
  }

  const activeSegments = Math.round((value / 100) * segments);

  return (
    <div
      className="segmented-progress-bar"
      style={{
        display: "flex",
        alignItems: "center",
        height: barHeight,
        background: backgroundColor,
        borderRadius: barRadius,
        border: `1px solid ${borderColor}`,
        padding: 2,
        width: "100%",
        boxSizing: "border-box",
        justifyContent: "flex-start",
      }}
    >
      {Array.from({ length: segments }).map((_, i) => (
        <div
          key={i}
          style={{
            width: segmentWidth,
            height: "80%",
            borderRadius: 2,
            marginLeft: i === 0 ? 0 : segmentGap,
            background: i < activeSegments ? getSegmentColor(i) : "#0a2340",
            transition: "background 0.3s ease",
            animation:
              i < activeSegments
                ? `segmentAppear 0.6s ease ${i * 0.05}s both`
                : "none",
            opacity: i < activeSegments ? 1 : 0.3,
          }}
        />
      ))}
      <style>{`
        @keyframes segmentAppear {
          from {
            opacity: 0;
            transform: scaleY(0.3);
          }
          to {
            opacity: 1;
            transform: scaleY(1);
          }
        }
      `}</style>
    </div>
  );
};

const getHealthStatusColor = (healthStatus) => {
  switch (healthStatus?.toLowerCase()) {
    case "healthy":
      return "#4caf50";
    case "warning":
      return "#ff9800";
    case "critical":
      return "#f44336";
    default:
      return "#9e9e9e";
  }
};

const getHealthStatusStyle = (healthStatus) => {
  const color = getHealthStatusColor(healthStatus);
  return {
    backgroundColor: color,
    color: "white",
    fontWeight: 600,
    fontSize: 12,
    padding: "2px 6px",
    borderRadius: 8,
    display: "inline-block",
    textAlign: "center",
    marginLeft: 8,
  };
};

const CustomLegend = ({
  channels,
  visibleSeries,
  onLegendClick,
  assetType,
  chartColors,
}) => {
  const channelColors = {};
  if (chartColors && channels) {
    channels.forEach((channel, index) => {
      channelColors[channel.id] = chartColors[index];
    });
  }

  return (
    <div
      className="custom-legend"
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "8px",
        padding: "4px 8px",
        // position: 'absolute',
        // top: '5px',
        // left: '49%',
        // transform: 'translateX(-50%)',
        zIndex: 10,
        fontFamily: "Inter, Arial, sans-serif",
        fontSize: "10px",
        fontWeight: "400",
        width: "370px",
        height: "100%",
        overflow: "auto",
        background: "rgba(255, 255, 255, 0.95)",
        borderRadius: "4px",
        // boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        border: "1px solid #e0e0e0",
      }}
    >
      {channels.map((channel, i) => (
        <span
          key={channel.id}
          onClick={() => onLegendClick(channel.id)}
          style={{
            cursor: "pointer",
            opacity: visibleSeries[channel.id] ? 1 : 0.4,
            display: "flex",
            alignItems: "center",
            whiteSpace: "nowrap",
            userSelect: "none",
            transition: "opacity 0.2s",
            padding: "2px 4px",
            borderRadius: "2px",
            "&:hover": {
              backgroundColor: "rgba(0,0,0,0.05)",
            },
          }}
        >
          <span
            style={{
              display: "inline-block",
              width: "8px",
              height: "8px",
              borderRadius: "50%",
              background: channelColors[channel.id] || "#4caf50",
              border: "1px solid #fff",
              boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
              marginRight: "4px",
            }}
          />
          <span
            style={{ color: "#333", fontWeight: 500, fontSize: "10px" }}
          >{channel.nameLocalized}{channel.unit ? ` [${channel.unit}]` : ''}</span>
        </span>
      ))}
    </div>
  );
};

const ChartsPanel = ({
  channels,
  chartData,
  assetType,
  onClose,
  isVisible,
  apiData,
  isTableDataLoading,
  onLoadTableData,
  hasLoadedTableData,
  asset,
  tablePagination,
  tableSorting,
  onSortChange,
  onLoadMoreTableData,
  filterType,
  setFilterType,
  metricsList,
  metricsData,
  metricsPagination,
  metricsLoading,
  isMetricsListLoading,
  onMetricPaginationChange,
  setMetricsPagination,
  onPointClick,
}) => {
  useEffect(() => {
    if (!isVisible || !channels || !chartData) return;

    const mapContainer = document.querySelector(
      ".maplibregl-canvas-container"
    );
    if (mapContainer) {
      const mapCanvas = mapContainer.querySelector("canvas");
      if (mapCanvas) {
        mapCanvas.style.display = "block";
        mapCanvas.style.visibility = "visible";
        mapCanvas.style.opacity = "1";
        mapCanvas.style.zIndex = "1";
        mapCanvas.style.position = "absolute";
        mapCanvas.style.top = "0";
        mapCanvas.style.left = "0";
      }
    }
  }, [isVisible, channels, chartData]);

  if (!isVisible) return null;

  const toggleFilterType = (e) => {
    const newFilterType = filterType === "charts" ? "dataTables" : "charts";
    setFilterType(newFilterType);

    if (newFilterType === "dataTables") {
      if (onLoadTableData) {
        onLoadTableData();
      }
    } else if (newFilterType === "charts") {
      setTimeout(() => {
        const mapContainer = document.querySelector(
          ".maplibregl-canvas-container"
        );
        if (mapContainer) {
          const mapCanvas = mapContainer.querySelector("canvas");
          if (mapCanvas) {
            mapCanvas.style.display = "block";
            mapCanvas.style.visibility = "visible";
            mapCanvas.style.opacity = "1";
            mapCanvas.style.zIndex = "1";
            mapCanvas.style.position = "absolute";
            mapCanvas.style.top = "0";
            mapCanvas.style.left = "0";
          }
        }

        if (window.mapInstance && window.mapInstance.resize) {
          window.mapInstance.resize();
        }
      }, 150);
    }
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  return ReactDOM.createPortal(
    <div
      className="charts-panel-container"
      style={{
        position: "fixed",
        left: "460px",
        top: "78px",
        height: "91%",
        background: "#fff",
        overflow: "hidden",
        padding: "10px 0px 0px 0px",
        boxSizing: "border-box",
        animation: "slideInFromLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1)",
        zIndex: 1202,
        isolation: "isolate",
        contain: "layout style paint",
        borderRadius: "12px",
      }}
    >
      <IconButton
        onClick={onClose}
        className="charts-panel-close-button"
        size="small"
        style={{
          backgroundColor: "rgba(255, 255, 255, 0.95)",
          borderRadius: "50%",
          width: "32px",
          height: "32px",
          minWidth: "32px",
          "&:hover": {
            backgroundColor: "#e3f2fd",
            transform: "scale(1.05)",
          },
          transition: "all 0.2s ease",
        }}
      >
        <CloseIcon style={{ fontSize: "22px", background: "none" }} />
      </IconButton>
      <div style={{ height: "100%", display: "flex", flexDirection: "column" }}>
        <Box
          className="charts-panel-header"
          style={{
            padding: "1px 60px 11px 0px",
            borderBottom: "1px solid #e0e0e0",
            marginBottom: "10px",
            flexShrink: 0,
          }}
        >
          <Box
            className="charts-panel-header-content"
            style={{ marginRight: "20px" }}
          >
            <Box className="charts-panel-header-top">
              <Typography
                style={{
                  fontSize: "1rem",
                  fontWeight: 400,
                  textTransform: "uppercase",
                  marginLeft: "20px",
                }}
              >
                {asset.componentName} | {asset.componentNumber}
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <SegmentedProgressBar
                  value={asset.healthScore || 0}
                  segments={20}
                  barHeight={20}
                  barRadius={8}
                  segmentWidth={8}
                  segmentGap={1}
                />
                <span
                  style={{
                    fontWeight: 500,
                    fontSize: 12,
                    minWidth: 30,
                    textAlign: "right",
                    color: "#1a237e",
                  }}
                >
                  {Math.round(asset.healthScore || 0)}%
                </span>
                <span style={getHealthStatusStyle(asset.healthStatus)}>
                  {asset.healthStatus}
                </span>
              </Box>
            </Box>
            <Box className="charts-panel-header-bottom">
              <div
                className={
                  filterType === "charts" ? "optionsSwitch" : "optionsSwitch in"
                }
                style={{ minWidth: "210px", height: "26px" }}
              >
                <div className="option" onClick={toggleFilterType}>
                  Charts
                </div>
                <div className="option" onClick={toggleFilterType}>
                  Data Tables
                </div>
              </div>
            </Box>
          </Box>
        </Box>

        <div
          style={{
            flex: 1,
            overflow: "auto",
            // paddingRight: '10px',
            marginTop: "10px",
          }}
        >
          {filterType === "charts" ? (
            <div
              id="charts-panel-portal"
              style={{
                display: "flex",
                flexWrap: "wrap",
                gap: "20px",
                width: "100%",

                paddingBottom: "20px",
                boxSizing: "border-box",
              }}
            >
              {channels?.map((channel, index) => {
                const colorSchemes = {
                  pump: ["#ff9800", "#f44336", "#8bc34a", "#cddc39"],
                  generator: ["#4caf50", "#2196f3", "#e91e63", "#f44336"],
                  compressor: ["#9c27b0", "#607d8b", "#e91e63", "#f44336"],
                  heat_exchanger: ["#e91e63", "#ff5722", "#8bc34a", "#f44336"],
                  motor: ["#00bcd4", "#8bc34a", "#e91e63", "#f44336"],
                  fan: ["#795548", "#9e9e9e", "#e91e63", "#f44336"],
                  transformer: ["#2196f3", "#ff9800", "#8bc34a", "#4caf50"],
                  boiler: ["#3f51b5", "#673ab7", "#8bc34a", "#f44336"],
                  conveyor: ["#ffc107", "#cddc39", "#e91e63", "#f44336"],
                  reactor: ["#ff5722", "#4caf50", "#f44336", "#8bc34a"],
                  cooling_tower: ["#00bcd4", "#009688", "#8bc34a", "#f44336"],
                  turbine: ["#f44336", "#e91e63", "#8bc34a", "#f44336"],
                };
                const colors = colorSchemes[assetType] || [
                  "#4caf50",
                  "#2196f3",
                  "#e91e63",
                  "#f44336",
                ];
                const color = colors[index % colors.length];

                return (
                  <Box
                    key={channel.id}
                    className="chart-card"
                    style={{
                      border: "1px solid #e0e0e0",
                      borderRadius: "12px",
                      padding: "16px",
                      background: "#fff",
                      // minHeight: '350px',
                      // minWidth: '280px',
                      flex: "1 1 calc(33.333% - 14px)",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                      transition: "all 0.3s ease",
                      cursor: "pointer",
                      boxSizing: "border-box",
                    }}
                  >
                    {channel.metric && (
                      <Typography
                        variant="subtitle2"
                        style={{
                          marginBottom: "8px",
                          fontWeight: 700,
                          color: "#1a237e",
                          fontSize: "16px",
                          textAlign: "center",
                          lineHeight: "1.4",
                        }}
                      >
                        {channel.metric}
                      </Typography>
                    )}
                    {channel.description && (
                      <Typography
                        variant="subtitle2"
                        style={{
                          marginBottom: "12px",
                          fontWeight: 600,
                          color: "#1a237e",
                          fontSize: "13px",
                          textAlign: "center",
                          lineHeight: "1.4",
                        }}
                      >
                        {channel.description}
                      </Typography>
                    )}
                    <div
                      className="chart-card-container"
                      style={{
                        // height: '300px',
                        width: "100%",
                        background: "#fff",
                        borderRadius: "8px",
                        // border: '1px solid #f0f0f0',
                        overflow: "hidden",
                        boxSizing: "border-box",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        position: "relative",
                        zIndex: 1,
                        contain: "layout style paint",
                        isolation: "isolate",
                      }}
                    >
                      <CustomChart
                        data={(chartData[channel.id] || [])
                          .sort((a, b) => a[0] - b[0])
                          .filter(
                            (_, index, array) =>
                              index === 0 ||
                              array[index][0] !== array[index - 1][0]
                          )}
                        width={
                          window.innerWidth <= 768
                            ? window.innerWidth * 0.95 - 40
                            : Math.min(400, window.innerWidth * 0.3)
                        }
                        height={window.innerWidth <= 768 ? 200 : 280}
                        color={color}
                        showGrid={true}
                        showPoints={true}
                        lineWidth={2}
                        onPointClick={onPointClick}
                      />
                    </div>
                  </Box>
                );
              })}
            </div>
          ) : (
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(1, 1fr)",
                gap: "20px",
                width: "100%",
                paddingBottom: "20px",
                boxSizing: "border-box",
              }}
            >
              {(() => {
                return isTableDataLoading && !apiData ? (
                  <Box
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      height: "400px",
                      width: "100%",
                    }}
                  >
                    <CircularProgress
                      size={40}
                      style={{
                        color: "#1a237e",
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                      }}
                    />
                  </Box>
                ) : metricsList && metricsList.length > 0 ? (
                  metricsList.map((metricObj, index) => {
                    const metricId = typeof metricObj === 'string' ? metricObj : metricObj.metricId;
                    const metricName = typeof metricObj === 'string' ? metricObj : metricObj.metric;
                    const dataArray = metricsData[metricId] || [];
                    return (
                      <Box
                        key={metricId}
                        className="data-table-card"
                        style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: "12px",
                          padding: "16px",
                          background: "#fff",
                          minHeight: "350px",
                          width: "100%",
                          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                          transition: "all 0.3s ease",
                          boxSizing: "border-box",
                        }}
                      >
                        {/* Metric title */}
                        <Typography
                          variant="subtitle2"
                          style={{
                            marginBottom: "12px",
                            fontWeight: 600,
                            color: "#1a237e",
                            fontSize: "16px",
                            textAlign: "center",
                            lineHeight: "1.4",
                          }}
                        >
                          {metricName}
                        </Typography>
                        <Typography
                          variant="subtitle2"
                          style={{
                            marginBottom: "12px",
                            fontWeight: 600,
                            color: "#1a237e",
                            fontSize: "13px",
                            textAlign: "center",
                            lineHeight: "1.4",
                          }}
                        >
                          {dataArray && dataArray.length > 0 ? dataArray[0].description || "" : ""}
                        </Typography>
                        <div
                          style={{
                            height: "300px",
                            width: "100%",
                            background: "#fff",
                            borderRadius: "8px",
                            border: "1px solid #f0f0f0",
                            overflow: "hidden",
                            boxSizing: "border-box",
                            position: "relative",
                            zIndex: 1,
                            contain: "layout style paint",
                            isolation: "isolate",
                          }}
                        >
                          {metricsLoading[metricId] ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                              }}
                            >
                              <CircularProgress
                                size={40}
                                style={{
                                  color: "#1a237e",
                                  position: "absolute",
                                  top: "50%",
                                  left: "50%",
                                }}
                              />
                            </div>
                          ) : dataArray && dataArray.length > 0 ? (
                            <div
                              style={{
                                height: "400px",
                                width: "100%",
                                overflow: "auto",
                              }}
                            >
                              <Table size="small" style={{ minWidth: 650 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#1a237e",
                                        padding: "8px",
                                        cursor: "pointer",
                                        userSelect: "none",
                                      }}
                                      onClick={() => onSortChange("Timestamp")}
                                    >
                                      Timestamp
                                      {tableSorting.sortBy === "Timestamp" && (
                                        <span style={{ marginLeft: "4px" }}>
                                          {tableSorting.isAscending ? "↑" : "↓"}
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#1a237e",
                                        padding: "8px",
                                        cursor: "pointer",
                                        userSelect: "none",
                                      }}
                                      onClick={() => onSortChange("Value")}
                                    >
                                      Value
                                      {tableSorting.sortBy === "Value" && (
                                        <span style={{ marginLeft: "4px" }}>
                                          {tableSorting.isAscending ? "↑" : "↓"}
                                        </span>
                                      )}
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#1a237e",
                                        padding: "8px",
                                      }}
                                    >
                                      Channel
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#1a237e",
                                        padding: "8px",
                                      }}
                                    >
                                      Channel unit
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#1a237e",
                                        padding: "8px",
                                      }}
                                    >
                                      Conditional Channel
                                    </TableCell>
                                    <TableCell
                                      style={{
                                        fontSize: "12px",
                                        fontWeight: 600,
                                        color: "#1a237e",
                                        padding: "8px",
                                      }}
                                    >
                                      Conditional Channel Unit
                                    </TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dataArray.map((item, index) => (
                                    <TableRow key={index} hover>
                                      <TableCell
                                        style={{
                                          fontSize: "11px",
                                          color: "#666",
                                          padding: "8px",
                                        }}
                                      >
                                        {formatTimestamp(item.timestamp)}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "11px",
                                          fontWeight: 500,
                                          color: "#1a237e",
                                          padding: "8px",
                                        }}
                                      >
                                        {item.value}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "11px",
                                          color: "#666",
                                          padding: "8px",
                                        }}
                                      >
                                        {item.channelName}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "11px",
                                          color: "#666",
                                          padding: "8px",
                                        }}
                                      >
                                        {item.channelUnit}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "11px",
                                          color: "#666",
                                          padding: "8px",
                                        }}
                                      >
                                        {item.conditionalChannelName}
                                      </TableCell>
                                      <TableCell
                                        style={{
                                          fontSize: "11px",
                                          color: "#666",
                                          padding: "8px",
                                        }}
                                      >
                                        {item.conditionalChannelUnit}
                                      </TableCell>
                                    </TableRow>
                                  ))}
                                </TableBody>
                              </Table>

                              {/* Pagination for this metric */}
                              {metricsPagination[metricId] &&
                                metricsPagination[metricId].totalCount >
                                  (metricsPagination[metricId].pageSize ||
                                    6) && (
                                  <Box
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <div
                                      style={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        padding: "16px",
                                        gap: "16px",
                                      }}
                                    >
                                      {/* Pagination info and controls */}
                                      <div
                                        style={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: "16px",
                                        }}
                                      >
                                        <span
                                          style={{
                                            fontSize: "14px",
                                            color: "#666",
                                          }}
                                        >
                                          {(() => {
                                            const currentPage =
                                              metricsPagination[metricId]
                                                ?.pageNumber || 1;
                                            const pageSize =
                                              metricsPagination[metricId]
                                                ?.pageSize || 6;
                                            const totalCount =
                                              metricsPagination[metricId]
                                                ?.totalCount || 0;
                                            const from =
                                              (currentPage - 1) * pageSize + 1;
                                            const to = Math.min(
                                              currentPage * pageSize,
                                              totalCount
                                            );
                                            return `${from}-${to} of ${totalCount}`;
                                          })()}
                                        </span>

                                        <div
                                          style={{
                                            display: "flex",
                                            gap: "8px",
                                          }}
                                        >
                                          <button
                                            onClick={() => {
                                              const currentPage =
                                                metricsPagination[metricId]
                                                  ?.pageNumber || 1;
                                              if (currentPage > 1) {
                                                onMetricPaginationChange(
                                                  metricId,
                                                  currentPage - 1
                                                );
                                              }
                                            }}
                                            disabled={
                                              !metricsPagination[metricId] ||
                                              (metricsPagination[metricId]
                                                ?.pageNumber || 1) <= 1
                                            }
                                            style={{
                                              padding: "4px 6px",
                                              border: "1px solid #ccc",
                                              borderRadius: "4px",
                                              backgroundColor:
                                                !metricsPagination[
                                                  metricId
                                                ] ||
                                                (metricsPagination[metricId]
                                                  ?.pageNumber || 1) <= 1
                                                  ? "#f5f5f5"
                                                  : "white",
                                              cursor:
                                                !metricsPagination[
                                                  metricId
                                                ] ||
                                                (metricsPagination[metricId]
                                                  ?.pageNumber || 1) <= 1
                                                  ? "not-allowed"
                                                  : "pointer",
                                              fontSize: "13px",
                                              height: "30px",
                                            }}
                                          >
                                            Previous
                                          </button>

                                          <button
                                            onClick={() => {
                                              const currentPage =
                                                metricsPagination[metricId]
                                                  ?.pageNumber || 1;
                                              const totalPages = Math.ceil(
                                                (metricsPagination[metricId]
                                                  ?.totalCount || 0) /
                                                  (metricsPagination[metricId]
                                                    ?.pageSize || 6)
                                              );
                                              if (currentPage < totalPages) {
                                                onMetricPaginationChange(
                                                  metricId,
                                                  currentPage + 1
                                                );
                                              }
                                            }}
                                            disabled={(() => {
                                              const currentPage =
                                                metricsPagination[metricId]
                                                  ?.pageNumber || 1;
                                              const totalPages = Math.ceil(
                                                (metricsPagination[metricId]
                                                  ?.totalCount || 0) /
                                                  (metricsPagination[metricId]
                                                    ?.pageSize || 6)
                                              );
                                              return (
                                                !metricsPagination[
                                                  metricId
                                                ] || currentPage >= totalPages
                                              );
                                            })()}
                                            style={{
                                              padding: "4px 6px",
                                              border: "1px solid #ccc",
                                              borderRadius: "4px",
                                              backgroundColor: (() => {
                                                const currentPage =
                                                  metricsPagination[metricId]
                                                    ?.pageNumber || 1;
                                                const totalPages = Math.ceil(
                                                  (metricsPagination[metricId]
                                                    ?.totalCount || 0) /
                                                    (metricsPagination[
                                                      metricId
                                                    ]?.pageSize || 6)
                                                );
                                                return !metricsPagination[
                                                  metricId
                                                ] || currentPage >= totalPages
                                                  ? "#f5f5f5"
                                                  : "white";
                                              })(),
                                              cursor: (() => {
                                                const currentPage =
                                                  metricsPagination[metricId]
                                                    ?.pageNumber || 1;
                                                const totalPages = Math.ceil(
                                                  (metricsPagination[metricId]
                                                    ?.totalCount || 0) /
                                                    (metricsPagination[
                                                      metricId
                                                    ]?.pageSize || 6)
                                                );
                                                return !metricsPagination[
                                                  metricId
                                                ] || currentPage >= totalPages
                                                  ? "not-allowed"
                                                  : "pointer";
                                              })(),
                                              fontSize: "13px",
                                              height: "30px",
                                            }}
                                          >
                                            Next
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  </Box>
                                )}
                            </div>
                          ) : (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                height: "100%",
                                color: "#666",
                                fontSize: "14px",
                                fontWeight: 500,
                                fontStyle: "italic",
                              }}
                            >
                              {metricsLoading[metricId] ? (
                                <CircularProgress
                                  size={40}
                                  style={{
                                    color: "#1a237e",
                                    position: "absolute",
                                    top: "50%",
                                    left: "50%",
                                  }}
                                />
                              ) : !metricsData[metricId] ? (
                                "No data available for this metric"
                              ) : (
                                "No data found"
                              )}
                            </div>
                          )}
                        </div>
                      </Box>
                    );
                  })
                ) : (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      height: "100%",
                      color: "#666",
                      fontSize: "14px",
                      fontWeight: 500,
                      fontStyle: "italic",
                    }}
                  >
                    {isMetricsListLoading ? (
                      <CircularProgress
                        size={40}
                        style={{
                          color: "#1a237e",
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                        }}
                      />
                    ) : !metricsList || metricsList.length === 0 ? (
                      'Click "Data Tables" to load metrics'
                    ) : (
                      "No data found"
                    )}
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
};

const AssetDetailModal = ({
  open,
  onClose,
  asset,
  filterTypeMetrics,
  onCloseStart,
}) => {
  const {
    setCurrentUnit,
    setCurrentUnitModel,
    changeUnitGlobalFilters,
    updateDateRange,
    units,
  } = useBoxes();

  const [visibleSeries, setVisibleSeries] = useState({});
  const [isClosing, setIsClosing] = useState(false);
  const closeTimeoutRef = useRef(null);
  const [showChartsPanel, setShowChartsPanel] = useState(false);
  const [apiData, setApiData] = useState(null);
  const [isTableDataLoading, setIsTableDataLoading] = useState(false);
  const [hasLoadedTableData, setHasLoadedTableData] = useState(false);
  const [filterType, setFilterType] = useState("charts");

  // Table pagination and sorting state
  const [tablePagination, setTablePagination] = useState({
    pageSize: 6,
    pageNumber: 1,
    hasMoreData: true,
    isLoadingMore: false,
    totalCount: 0,
  });

  const [tableSorting, setTableSorting] = useState({
    sortBy: "Timestamp",
    isAscending: false,
  });

  // New states for metrics tables
  const [metricsList, setMetricsList] = useState([]);
  const [metricsData, setMetricsData] = useState({});
  const [metricsPagination, setMetricsPagination] = useState({});
  const [metricsLoading, setMetricsLoading] = useState({});
  const [isMetricsListLoading, setIsMetricsListLoading] = useState(false);

  const assetWithChartData = useMemo(() => {
    if (!asset) return null;

    const parseDateToTimestamp = (dateString) => {
      return new Date(dateString + "T00:00:00Z").getTime();
    };

    const issuesByChannel = {};

    if (asset.issues && Array.isArray(asset.issues)) {
      asset.issues.forEach((issue) => {
        const channelKey = issue.channelName || issue.metric;
        if (!issuesByChannel[channelKey]) {
          issuesByChannel[channelKey] = [];
        }
        issuesByChannel[channelKey].push({
          timestamp: new Date(
            issue.dateTimeUTC.replace(" ", "T") + "Z"
          ).getTime(),
          value: parseFloat(issue.value),
          metric: issue.metric,
          condition: issue.condition,
          channelNameUser: issue.channelNameUser,
          channelUnit: issue.channelUnit,
        });
      });
    }

    if (asset.metricSummaries && Array.isArray(asset.metricSummaries)) {
      asset.metricSummaries.forEach((metricSummary) => {
        if (metricSummary.issues && typeof metricSummary.issues === "object") {
          const firstEntry = Object.values(metricSummary.issues)[0];
          if (firstEntry && typeof firstEntry === "object" && firstEntry.channels) {
            Object.entries(metricSummary.issues).forEach(([date, issueData]) => {
              if (issueData && issueData.channels && Array.isArray(issueData.channels)) {
                // Use metricId for uniqueness (multiple metrics can have same name)
                const channelKey = metricSummary.metricId || metricSummary.metric;
                if (!issuesByChannel[channelKey]) {
                  issuesByChannel[channelKey] = [];
                }
                
                const timestamp = issueData.dateFrom || parseDateToTimestamp(date);
                
                issuesByChannel[channelKey].push({
                  timestamp: timestamp,
                  value: parseInt(issueData.count) || 0,
                  metric: metricSummary.metric,
                  metricId: metricSummary.metricId,
                  condition: metricSummary.metric,
                  channelNameUser: metricSummary.metric,
                  channelUnit: "count",
                  channels: issueData.channels,
                  dateFrom: issueData.dateFrom,
                  dateTo: issueData.dateTo,
                  description: metricSummary.description,
                  totalIssues: metricSummary.totalIssues,
                });
              }
            });
          } else {
            // Use metricId for uniqueness (multiple metrics can have same name)
            const channelKey = metricSummary.metricId || metricSummary.metric;
            if (!issuesByChannel[channelKey]) {
              issuesByChannel[channelKey] = [];
            }

            Object.entries(metricSummary.issues).forEach(([date, count]) => {
              issuesByChannel[channelKey].push({
                timestamp: parseDateToTimestamp(date),
                value: parseInt(count),
                metric: metricSummary.metric,
                metricId: metricSummary.metricId,
                condition: metricSummary.metric,
                channelNameUser: metricSummary.metric,
                channelUnit: "count",
                description: metricSummary.description,
                totalIssues: metricSummary.totalIssues,
              });
            });
          }
        } else if (
          metricSummary.issues &&
          Array.isArray(metricSummary.issues)
        ) {
          metricSummary.issues.forEach((issue) => {
            const channelKey = issue.channelName || metricSummary.metric;
            if (!issuesByChannel[channelKey]) {
              issuesByChannel[channelKey] = [];
            }
            issuesByChannel[channelKey].push({
              timestamp: new Date(
                issue.dateTimeUTC.replace(" ", "T") + "Z"
              ).getTime(),
              value: parseFloat(issue.value),
              metric: issue.metric,
              condition: issue.condition,
              channelNameUser: issue.channelNameUser,
              channelUnit: issue.channelUnit,
              totalIssues: metricSummary.totalIssues,
            });
          });
        }
      });
    }

    const chartData = {};
    const channels = [];
    const pointChannelsMap = {};

    Object.keys(issuesByChannel).forEach((channelName, index) => {
      const channelIssues = issuesByChannel[channelName];

        if (channelIssues.length > 0) {
        const firstIssue = channelIssues[0];

        const channelDisplayName = firstIssue.metric && firstIssue.totalIssues !== undefined 
          ? `${firstIssue.metric} [${firstIssue.totalIssues}]`
          : firstIssue.description || channelName;

        const channelId = `channel_${index}`;
        chartData[channelId] = channelIssues.map((issue) => {
          if (issue.channels && Array.isArray(issue.channels)) {
            const mapKey = `${channelId}_${issue.timestamp}`;
            pointChannelsMap[mapKey] = {
              channels: issue.channels,
              dateFrom: issue.dateFrom,
              dateTo: issue.dateTo,
            };
          }
          return [
            issue.timestamp,
            issue.value,
          ];
        });

        channels.push({
          id: channelId,
          nameLocalized: channelDisplayName,
          channelName: channelName,
          metric: firstIssue.metric,
          description: firstIssue.description,
        });
      }
    });

    const allTimestamps = Object.values(chartData).flatMap((data) =>
      data.map(([timestamp]) => timestamp)
    );
    const startTime =
      allTimestamps.length > 0
        ? Math.min(...allTimestamps)
        : Date.now() - 3600000;

    return {
      ...asset,
      chartData,
      channels,
      type: "pump",
      startTime,
      pointChannelsMap,
      issuesByChannel,
    };
  }, [asset]);

  const closeModal = useCallback(() => {
    setIsClosing(true);

    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
    }

    closeTimeoutRef.current = setTimeout(() => {
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";

      setIsClosing(false);
      setShowChartsPanel(false);
      setIsTableDataLoading(false);
      setHasLoadedTableData(false);
      onClose();
      closeTimeoutRef.current = null;
    }, 400);
  }, [onClose]);

  const handleClose = useCallback(() => {
    // Миттєво повідомляємо батьківський компонент про початок закриття
    if (onCloseStart) {
      onCloseStart();
    }

    if (showChartsPanel) {
      setShowChartsPanel(false);
      setTimeout(() => {
        closeModal();
      }, 300);
    } else {
      closeModal();
    }
  }, [showChartsPanel, closeModal, onCloseStart]);

  const loadTableData = useCallback(
    async (isLoadMore = false) => {
      if (!asset || !asset.unitUId) return;

      try {
        if (!isLoadMore) {
          setIsTableDataLoading(true);
          setTablePagination((prev) => ({
            ...prev,
            pageNumber: 1,
            hasMoreData: true,
          }));
        } else {
          setTablePagination((prev) => ({ ...prev, isLoadingMore: true }));
        }

        const currentPage = isLoadMore ? tablePagination.pageNumber + 1 : 1;

        const sortRequest = {
          sortBy: tableSorting.sortBy,
          isAscending: tableSorting.isAscending,
          pageSize: tablePagination.pageSize,
          pageNumber: currentPage,
        };

        const response = await MmsApiService.getRawHealthDataPaged(
          asset.unitUId,
          sortRequest,
          filterTypeMetrics
        );

        if (response && response.data) {
          const newData = response.data;

          if (isLoadMore) {
            setApiData((prevData) => {
              const mergedData = { ...prevData };
              if (newData.items && newData.items.length > 0) {
                newData.items.forEach((item) => {
                  const metricName = item.metric || "Unknown Metric";
                  if (mergedData[metricName]) {
                    mergedData[metricName] = [...mergedData[metricName], item];
                  } else {
                    mergedData[metricName] = [item];
                  }
                });
              }
              return mergedData;
            });
            const hasMoreData =
              newData.items && newData.items.length >= tablePagination.pageSize;
            setTablePagination((prev) => ({
              ...prev,
              pageNumber: currentPage,
              isLoadingMore: false,
              hasMoreData: hasMoreData,
            }));
          } else {
            const groupedData = {};
            if (newData.items && newData.items.length > 0) {
              newData.items.forEach((item) => {
                const metricName = item.metric || "Unknown Metric";
                if (groupedData[metricName]) {
                  groupedData[metricName].push(item);
                } else {
                  groupedData[metricName] = [item];
                }
              });
            }

            setApiData(groupedData);
            const hasMoreData =
              newData.items && newData.items.length >= tablePagination.pageSize;
            setTablePagination((prev) => ({
              ...prev,
              pageNumber: 1,
              hasMoreData: hasMoreData,
              totalCount: newData.itemsTotalCount || 0,
            }));
            setHasLoadedTableData(true);
          }
        }
      } catch (error) {
        console.error("Error fetching table data:", error);
      } finally {
        if (!isLoadMore) {
          setIsTableDataLoading(false);
        }
      }
    },
    [
      asset,
      filterTypeMetrics,
      tablePagination.pageSize,
      tablePagination.pageNumber,
      tableSorting,
    ]
  );

  const handleSortChange = useCallback(
    (sortBy) => {
      setTableSorting((prev) => ({
        ...prev,
        sortBy,
        isAscending: prev.sortBy === sortBy ? !prev.isAscending : false,
      }));
      setTablePagination((prev) => ({
        ...prev,
        pageNumber: 1,
        hasMoreData: true,
      }));
      setApiData(null);
      setHasLoadedTableData(false);

      setTimeout(() => {
        loadTableData(false);
      }, 100);
    },
    [loadTableData]
  );

  const loadMoreTableData = useCallback(() => {
    if (!tablePagination.isLoadingMore && tablePagination.hasMoreData) {
      loadTableData(true);
    }
  }, [
    loadTableData,
    tablePagination.isLoadingMore,
    tablePagination.hasMoreData,
  ]);

  // Load data for specific metric
  const loadMetricData = useCallback(
    async (metricId, pageNumber = 1) => {
      if (!asset || !asset.unitUId || !metricId) return;

      try {
        setMetricsLoading((prev) => ({ ...prev, [metricId]: true }));

        const sortRequest = {
          sortBy: tableSorting.sortBy,
          isAscending: tableSorting.isAscending,
          pageSize: 6,
          pageNumber: pageNumber,
          metricId: metricId,
        };

        const response = await MmsApiService.getRawHealthDataPaged(
          asset.unitUId,
          sortRequest,
          filterTypeMetrics
        );

        if (response && response.data) {
          const newData = response.data;

          setMetricsData((prev) => ({
            ...prev,
            [metricId]: newData.items || [],
          }));

          setMetricsPagination((prev) => {
            const newPagination = {
              ...prev,
              [metricId]: {
                ...prev[metricId],
                pageSize: 6,
                pageNumber: pageNumber,
                totalCount: newData.itemsTotalCount || 0,
              },
            };
            return newPagination;
          });
        }
      } catch (error) {
        console.error(`Error loading data for metricId ${metricId}:`, error);
      } finally {
        setMetricsLoading((prev) => ({ ...prev, [metricId]: false }));
      }
    },
    [asset, filterTypeMetrics, tableSorting]
  );

  const loadMetricsList = useCallback(async () => {
    if (!asset || !asset.unitUId || !asset.metricSummaries) return;

    try {
      setIsMetricsListLoading(true);
      const metrics = asset.metricSummaries.map((summary) => ({
        metricId: summary.metricId,
        metric: summary.metric,
      }));
      
      setMetricsList(metrics);

      const initialPagination = {};
      metrics.forEach((metricObj) => {
        initialPagination[metricObj.metricId] = {
          pageSize: 6,
          pageNumber: 1,
          totalCount: 0,
        };
      });
      setMetricsPagination((prev) => ({ ...prev, ...initialPagination }));

      metrics.forEach((metricObj) => {
        loadMetricData(metricObj.metricId, 1);
      });
    } catch (error) {
      console.error("Error loading metrics list:", error);
    } finally {
      setIsMetricsListLoading(false);
    }
  }, [asset, loadMetricData]);

  const handleMetricPaginationChange = useCallback(
    (metricId, pageNumber) => {
      loadMetricData(metricId, pageNumber);
    },
    [loadMetricData]
  );

  const handleShowChartsPanel = () => {
    const newShowChartsPanel = !showChartsPanel;
    setShowChartsPanel(newShowChartsPanel);

    if (!newShowChartsPanel) {
      setTimeout(() => {
        const mapContainer = document.querySelector(
          ".maplibregl-canvas-container"
        );
        if (mapContainer) {
          const mapCanvas = mapContainer.querySelector("canvas");
          if (mapCanvas) {
            mapCanvas.style.display = "block";
            mapCanvas.style.visibility = "visible";
            mapCanvas.style.opacity = "1";
            mapCanvas.style.zIndex = "1";
            mapCanvas.style.position = "absolute";
            mapCanvas.style.top = "0";
            mapCanvas.style.left = "0";
          }
        }

        if (window.mapInstance && window.mapInstance.resize) {
          window.mapInstance.resize();
        }
      }, 100);
    }
  };

  const handleLegendClick = (channelId) => {
    setVisibleSeries((prev) => {
      const newVisibleSeries = { ...prev };

      const visibleCount =
        Object.values(newVisibleSeries).filter(Boolean).length;

      if (newVisibleSeries[channelId] === undefined) {
        newVisibleSeries[channelId] = true;
      } else {
        if (visibleCount === 1 && newVisibleSeries[channelId] === true) {
          return prev;
        }

        newVisibleSeries[channelId] = !newVisibleSeries[channelId];
      }

      return newVisibleSeries;
    });
  };

  useEffect(() => {
    if (assetWithChartData && assetWithChartData.channels) {
      const initialVisibility = {};
      assetWithChartData.channels.forEach((channel) => {
        initialVisibility[channel.id] = true;
      });
      setVisibleSeries(initialVisibility);

      setShowChartsPanel(false);
    }
  }, [assetWithChartData]);

  useEffect(() => {
    if (open) {
      setShowChartsPanel(false);
      setIsTableDataLoading(false);
      setHasLoadedTableData(false);
      setApiData(null);
    }
  }, [open, asset]);

  useEffect(() => {
    if (assetWithChartData && assetWithChartData.channels) {
      setVisibleSeries((prev) => {
        const newVisibility = { ...prev };
        let hasChanges = false;

        assetWithChartData.channels.forEach((channel) => {
          if (newVisibility[channel.id] === undefined) {
            newVisibility[channel.id] = true;
            hasChanges = true;
          }
        });

        Object.keys(newVisibility).forEach((channelId) => {
          const channelExists = assetWithChartData.channels.some(
            (channel) => channel.id === channelId
          );
          if (!channelExists) {
            delete newVisibility[channelId];
            hasChanges = true;
          }
        });

        return hasChanges ? newVisibility : prev;
      });

      setShowChartsPanel(false);
      setIsTableDataLoading(false);
      setHasLoadedTableData(false);
      setApiData(null);
    }
  }, [assetWithChartData]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape" && open) {
        handleClose();
      }
    };

    if (open) {
      document.addEventListener("keydown", handleEscape);
    }

    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [open, handleClose, showChartsPanel]);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      document.body.style.overflow = "";
      document.documentElement.style.overflow = "";
    };
  }, []);

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const handlePointClick = useCallback((point) => {
    if (!asset || !asset.unitUId) {
      return;
    }

    const unitModel = units?.find(unit => unit.uId === asset.unitUId);
    
    if (unitModel && unitModel.id) {
      setCurrentUnit(unitModel.id);
      setCurrentUnitModel(unitModel);
    } else {
      setCurrentUnit(asset.unitUId);
    }

    const pointTimestamp = point.x;
    const clickedDate = new Date(pointTimestamp);
    
    let channelsToOpen = null;
    let dateFrom = null;
    let dateTo = null;
    
    if (assetWithChartData && assetWithChartData.pointChannelsMap && assetWithChartData.chartData) {
      let closestChannelId = null;
      let closestChannelDistance = Infinity;
      const pointValue = point.y;
      const timeThreshold = 24 * 60 * 60 * 1000;
      
      Object.keys(assetWithChartData.chartData).forEach(channelId => {
        const channelPoints = assetWithChartData.chartData[channelId] || [];
        channelPoints.forEach(([timestamp, value]) => {
          const timeDiff = Math.abs(timestamp - pointTimestamp);
          const valueDiff = Math.abs(value - pointValue);
          
          const normalizedTimeDiff = timeDiff / timeThreshold;
          const normalizedValueDiff = valueDiff / (Math.abs(pointValue) || 1);
          const combinedDistance = normalizedTimeDiff + normalizedValueDiff * 0.1;
          
          if (timeDiff < timeThreshold && combinedDistance < closestChannelDistance) {
            closestChannelDistance = combinedDistance;
            closestChannelId = channelId;
          }
        });
      });
      
      if (closestChannelId) {
        let closestMapEntry = null;
        let minTimeDiff = Infinity;
        
        Object.keys(assetWithChartData.pointChannelsMap).forEach(key => {
          if (key.startsWith(`${closestChannelId}_`)) {
            const parts = key.split('_');
            if (parts.length >= 2) {
              const mapTimestamp = parseInt(parts[parts.length - 1]);
              if (!isNaN(mapTimestamp)) {
                const timeDiff = Math.abs(mapTimestamp - pointTimestamp);
                if (timeDiff < minTimeDiff) {
                  minTimeDiff = timeDiff;
                  closestMapEntry = assetWithChartData.pointChannelsMap[key];
                }
              }
            }
          }
        });
        
        if (closestMapEntry && closestMapEntry.channels && closestMapEntry.channels.length > 0) {
          channelsToOpen = closestMapEntry.channels;
          if (closestMapEntry.dateFrom) {
            dateFrom = new Date(closestMapEntry.dateFrom);
          }
          if (closestMapEntry.dateTo) {
            dateTo = new Date(closestMapEntry.dateTo);
          }
        }
      }
    }
    
    if (!channelsToOpen && assetWithChartData && assetWithChartData.issuesByChannel) {
      let closestIssue = null;
      let minTimeDiff = Infinity;
      
      Object.values(assetWithChartData.issuesByChannel).forEach((issues) => {
        issues.forEach((issue) => {
          if (issue.channels && Array.isArray(issue.channels)) {
            const timeDiff = Math.abs(issue.timestamp - pointTimestamp);
            if (timeDiff < minTimeDiff) {
              minTimeDiff = timeDiff;
              closestIssue = issue;
            }
          }
        });
      });
      
      if (closestIssue && closestIssue.channels) {
        channelsToOpen = closestIssue.channels;
        if (closestIssue.dateFrom) {
          dateFrom = new Date(closestIssue.dateFrom);
        }
        if (closestIssue.dateTo) {
          dateTo = new Date(closestIssue.dateTo);
        }
      }
    }
    
    if (channelsToOpen && channelsToOpen.length > 0) {
      try {
        localStorage.setItem('healthCheckChannelsToOpen', JSON.stringify(channelsToOpen));
      } catch (e) {
        console.error('Failed to store channels in localStorage:', e);
      }
    }
    
    if (!dateFrom) {
      dateFrom = new Date(clickedDate);
      dateFrom.setHours(0, 0, 0, 0);
    }
    
    if (!dateTo) {
      dateTo = new Date(clickedDate);
      dateTo.setHours(23, 59, 59, 999);
    }

    try {
      const unitIdToSave = unitModel && unitModel.id ? unitModel.id : asset.unitUId;
      localStorage.setItem('unitsPageUnitId', unitIdToSave);
      localStorage.setItem('unitsPageDateFrom', dateFrom.toISOString());
      localStorage.setItem('unitsPageDateTo', dateTo.toISOString());
      localStorage.setItem('unitsPageFilters', JSON.stringify({
        dateType: 'customRange',
        jobId: '',
        dateFrom: dateFrom.toISOString(),
        dateTo: dateTo.toISOString(),
      }));
      if (unitModel) {
        localStorage.setItem('unitsPageUnitModel', JSON.stringify(unitModel));
      }
    } catch (e) {
      console.error('Failed to store data in localStorage:', e);
    }

    updateDateRange(dateFrom, dateTo);

    changeUnitGlobalFilters({
      dateType: 'customRange',
      jobId: '',
      dateFrom: dateFrom,
      dateTo: dateTo,
    });

    window.open('/units', '_blank');
  }, [asset, units, setCurrentUnit, setCurrentUnitModel, changeUnitGlobalFilters, updateDateRange, assetWithChartData]);

  if (!asset || !assetWithChartData) return null;

  return (
    <>
      {open && !isClosing && (
        <>
          <div
            style={{
              position: "fixed",
              top: "78px",
              left: "450px",
              right: 0,
              bottom: 0,
              backgroundColor: "transparent",
              zIndex: 1200,
              animation: "fadeIn 0.3s ease-in-out",
            }}
            onClick={handleClose}
          />

          <div
            className="main-modal-container"
            data-show-charts={showChartsPanel}
          >
            <style>{`
        @keyframes slideInFromLeft {
          from {
            transform: translateX(-100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes slideInFromRight {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        .modal-content {
          animation: contentSlideIn 1s ease-out 0.1s both;
        }
        
        .modal-header {
          animation: headerSlideIn 0.4s ease-out 0.05s both;
        }
        
        @keyframes contentSlideIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes headerSlideIn {
          from {
            opacity: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        .chart-card {
          transition: all 0.3s ease;
        }
        
        // .chart-card:hover {
        //   transform: translateY(-2px);
        //   box-shadow: 0 6px 16px rgba(0,0,0,0.15) !important;
        // }
        
        /* Prevent chart styles from affecting map */
        [id^="container_chart_"] {
          contain: layout style paint;
          isolation: isolate;
        }
        
        [id^="container_chart_"] canvas {
          contain: layout style paint;
        }
        
        /* Ensure LightningChart canvases don't interfere with map */
        canvas[data-lcjs-chart] {
          contain: layout style paint;
          isolation: isolate;
          z-index: 1;
        }
        
        /* Force map canvas to stay on top */
        .maplibregl-canvas {
          z-index: 1 !important;
          position: relative !important;
        }
        
        /* Virtualized table styles */
        .virtualized-table-row {
          transition: background-color 0.2s ease;
        }
        
        .virtualized-table-row:hover {
          background-color: #f5f5f5;
        }
        
        .virtualized-table-header {
          position: sticky;
          top: 0;
          background: #f5f5f5;
          z-index: 2;
          border-bottom: 1px solid #ddd;
        }

        /* Responsive chart cards */
        @media (max-width: 1400px) {
          .chart-card {
            flex: 1 1 calc(50% - 10px) !important;
            min-width: 280px !important;
          }
        }
        
        @media (max-width: 800px) {
          .chart-card {
            flex: 1 1 100% !important;
            min-width: 250px !important;
          }
          .modal-header {
            padding-top: 0px !important;
            padding-right: 10px !important;
          }  
        }

        /* Responsive chart width */
        .chart-card canvas {
          max-width: 100% !important;
          height: auto !important;
        }

        @media (max-width: 1200px) {
          .chart-card canvas {
            max-width: 100% !important;
            height: auto !important;
          }
        }

        /* Responsive charts panel */
        @media (max-width: 1400px) {
          #charts-panel-portal {
            max-width: 900px !important;
          }
        }
        
        @media (max-width: 1200px) {
          #charts-panel-portal {
            max-width: 700px !important;
          }
        }
        
        @media (max-width: 1000px) {
          #charts-panel-portal {
            max-width: 500px !important;
          }
        }
        
        @media (max-width: 800px) {
          #charts-panel-portal {
            max-width: 100% !important;
          }
        }

        /* Charts panel container base styles */
        .charts-panel-container {
          width: calc(100vw - 470px);
          max-width: 1400px;
          min-width: 300px;
          left: 460px;
        }

        /* Responsive charts panel container */
        @media (max-width: 1600px) {
          .charts-panel-container {
            width: calc(100vw - 480px) !important;
            max-width: 1200px !important;
            top: 78px !important;
          }
        }
        
        @media (max-width: 1400px) {
          .charts-panel-container {
            width: calc(100vw - 480px) !important;
            max-width: 1000px !important;
          }
        }
        
        @media (max-width: 1200px) {
          .charts-panel-container {
            width: calc(100vw - 480px) !important;
            max-width: 800px !important;
          }
        }
        
        @media (max-width: 1000px) {
          .charts-panel-container {
            width: calc(100vw - 480px) !important;
            max-width: 600px !important;
            top: 87px !important;
          }
        }
        
        @media (max-width: 800px) {
          .charts-panel-container {
            width: calc(100vw - 20px) !important;
            left: 10px !important;
            max-width: none !important;
            padding-left: 20px !important;
            padding-top: 5px !important;
          }
        }

        /* Main modal container base styles */
        .main-modal-container {
          position: fixed;
          left: 460px;
          top: 78px;
          bottom: 0px;
          width: 450px;
          min-width: 450px;
          max-width: 450px;
          height: 90.2%;
          margin: 0px;
          border-radius: 12px;
          overflow: hidden;
          transform: translateX(0);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          animation: slideInFromLeft 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          background-color: #fff;
          z-index: 1201;
        }

        @media (max-width: 992px) {
          .main-modal-container {
            left: 10px !important;
            width: calc(100vw - 40px) !important;
            min-width: 300px !important;
            top: 87px;
          }
        }

        @media (max-width: 768px) {
          .main-modal-container {
            width: 95% !important;
            left: 2.5% !important;
            min-width: auto !important;
          }
          
          /* Show asset details panel on small screens even when charts panel is open */
          .main-modal-container[data-show-charts="true"] .asset-details-panel {
            display: block !important;
            position: relative !important;
            width: 100% !important;
            height: auto !important;
            right: auto !important;
            top: auto !important;
            z-index: 1202 !important;
          }

          .asset-details-panel {
            display: block !important;
            position: relative !important;
            width: 95% !important;
            min-width: auto !important;
            max-width: none !important;
            height: auto !important;
            right: auto !important;
            top: auto !important;
            z-index: 1202 !important;
            padding: 0px !important;
            padding-left: 20px !important;
          }
          
          /* Adjust charts panel positioning on small screens */
          #charts-panel-portal {
            left: 20px !important;
            width: calc(100vw - 40px) !important;
            max-width: none !important;
          }
        }

        @media (max-width: 768px) {
          /* Adjust charts panel positioning on very small screens */
          #charts-panel-portal {
            left: 2.5% !important;
            width: 95% !important;
            max-width: none !important;
          }
          
          /* CustomLegend responsive styles */
          .custom-legend {
            width: auto !important;
            max-width: 100% !important;
          }
          
          /* SegmentedProgressBar responsive styles */
          .segmented-progress-bar {
            height: 16px !important;
            padding: 1px !important;
          }
          
          .segmented-progress-bar > div {
            width: 6px !important;
            height: 70% !important;
            margin-left: 0.5px !important;
          }
          
          /* Main chart container responsive styles */
          .main-chart-container {
            width: 100% !important;
            max-width: 100% !important;
          }
          
          .main-chart-container canvas {
            max-width: 100% !important;
            height: auto !important;
          }
          
          /* Chart cards responsive styles */
          .chart-card canvas {
            max-width: 100% !important;
            height: auto !important;
          }
          
          /* Chart card containers responsive styles */
          .chart-card-container {
            height: 250px !important;
          }
          
          .chart-card-container canvas {
            max-width: 100% !important;
            height: auto !important;
          }
        }

        /* Close button positioning */
        .charts-panel-close-button {
          position: absolute !important;
          top: 8px !important;
          right: 10px !important;
          z-index: 1203 !important;
        }

        @media (max-width: 1200px) {
          .charts-panel-close-button {
            top: 10px !important;
            right: 15px !important;
          }
        }

        @media (max-width: 800px) {
          .charts-panel-close-button {
            top: 0px !important;
            right: 10px !important;
          }
        }

        /* Responsive header panel */
        .charts-panel-header {
          display: flex !important;
          flex-direction: column !important;
          gap: 15px !important;
        }

        .charts-panel-header-content {
          display: flex !important;
          flex-direction: row !important;
          align-items: center !important;
          justify-content: space-between !important;
          flex-wrap: wrap !important;
          gap: 15px !important;
          width: 100% !important;
        }

        .charts-panel-header-top {
          display: flex !important;
          align-items: center !important;
          gap: 20px !important;
          flex-wrap: wrap !important;
        }

        .charts-panel-header-bottom {
          display: flex !important;
          align-items: center !important;
          gap: 15px !important;
          flex-wrap: wrap !important;
        }

        @media (max-width: 1200px) {
          .charts-panel-header-content {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 15px !important;
          }
          
          .charts-panel-header-top {
            width: 100% !important;
            justify-content: space-between !important;
          }
          
          .charts-panel-header-bottom {
            width: 100% !important;
            justify-content: space-between !important;
          }
        }

        @media (max-width: 800px) {
          .charts-panel-header-top {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
          }
          
          .charts-panel-header-bottom {
            flex-direction: column !important;
            align-items: flex-start !important;
            gap: 10px !important;
          }
        }

      `}</style>
            <DialogTitle
              className="modal-header"
              style={{
                padding: "10px 20px 8px 20px",
                borderBottom: "1px solid #e0e0e0",
                marginBottom: "10px",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography
                  style={{
                    fontSize: "1rem",
                    fontWeight: 400,
                    textTransform: "uppercase",
                  }}
                >
                  Asset Details
                </Typography>
                <IconButton onClick={handleClose} size="small" fontSize="22px">
                  <CloseIcon />
                </IconButton>
              </Box>
            </DialogTitle>

            <DialogContent
              className="modal-content"
              style={{
                padding: "0",
                height: "100%",
                overflow: "hidden",
                display: "flex",
              }}
            >
              {/* Main Content Panel */}
              {(!showChartsPanel || window.innerWidth <= 992) && (
                <div
                  className="asset-details-panel"
                  style={{
                    position: "absolute",
                    right: "0",
                    top: "0",
                    width: "450px",
                    minWidth: "450px",
                    maxWidth: "450px",
                    height: "100%",
                    padding: "0 20px 20px 20px",
                    overflowY: "auto",
                    background: "#fff",
                    zIndex: 2,
                  }}
                >
                  <Grid container spacing={2}>
                    {asset.image && (
                      <Grid item xs={12}>
                        <Box
                          style={{
                            border: "1px solid #e0e0e0",
                            borderRadius: 8,
                            overflow: "hidden",
                            aspectRatio: "16/9",
                            width: "100%",
                            padding: 0,
                            margin: 0,
                          }}
                        >
                          <img
                            src={asset.image}
                            alt={asset.componentName}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                              display: "block",
                            }}
                          />
                        </Box>
                      </Grid>
                    )}

                    <Grid item xs={12}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography
                          variant="h5"
                          style={{ fontWeight: 600, color: "#1a237e" }}
                        >
                          {asset.componentName}
                        </Typography>
                        <span
                          style={{
                            backgroundColor:
                              asset.healthStatus === "Healthy"
                                ? "#4caf50"
                                : asset.healthStatus === "Warning"
                                ? "#ff9800"
                                : asset.healthStatus === "Critical"
                                ? "#f44336"
                                : "#9e9e9e",
                            color: "white",
                            borderRadius: 8,
                            padding: "2px 6px",
                            fontWeight: 600,
                            fontSize: 12,
                            display: "inline-block",
                            textAlign: "center",
                            marginLeft: 8,
                            marginTop: 8,
                          }}
                        >
                          {asset.healthStatus}
                        </span>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box display="flex" alignItems="center" gap={2}>
                        <Box flex={1} display="flex" alignItems="center">
                          <SegmentedProgressBar
                            value={asset.healthScore || 0}
                          />
                          <span
                            style={{
                              marginLeft: 12,
                              fontWeight: 500,
                              fontSize: 14,
                              minWidth: 38,
                              textAlign: "right",
                            }}
                          >
                            {Math.round(asset.healthScore || 0)}%
                          </span>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 8,
                          padding: "16px",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          style={{
                            marginBottom: "10px",
                            fontWeight: 600,
                            color: "#1a237e",
                          }}
                        >
                          Description
                        </Typography>
                        <Typography
                          variant="body2"
                          style={{ lineHeight: 1.6, color: "#424242" }}
                        >
                          {asset.assetTypeDescription || "N/A"}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 8,
                          padding: "16px",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          style={{
                            marginBottom: "12px",
                            fontWeight: 600,
                            color: "#1a237e",
                          }}
                        >
                          Asset Information
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Component Name:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              {asset.componentName}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Component Number:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              {asset.componentNumber || "N/A"}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Component ID:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{
                                fontWeight: 600,
                                color: "#1a237e",
                                textAlign: "right",
                              }}
                            >
                              {asset.componentId}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Department ID:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{
                                fontWeight: 600,
                                color: "#1a237e",
                                textAlign: "right",
                              }}
                            >
                              {asset.departmentId}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Department Name:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              {asset.departmentName}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Unit UID:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              {asset.unitUId}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Health Status:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              {asset.healthStatus}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Last issue date:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              {formatTimestamp(asset.lastHealthCheckDataDate)}
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Health Score:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              {asset.healthScore?.toFixed(1)}%
                            </Typography>
                          </Box>
                          {/* <Box display="flex" justifyContent="space-between" alignItems="center">
                  <Typography variant="caption" style={{ fontWeight: 500, color: '#666' }}>
                    Last Check:
                  </Typography>
                  <Typography variant="caption" style={{ fontWeight: 600, color: '#1a237e' }}>
                    {asset.checkDate ? new Date(asset.checkDate).toLocaleString() : 'N/A'}
                  </Typography>
                </Box> */}
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Location:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              {asset.jobProperties
                                ? (() => {
                                    const lat = asset.jobProperties.find(
                                      (prop) =>
                                        prop.name === "Position_Latitude"
                                    )?.value;
                                    const lng = asset.jobProperties.find(
                                      (prop) =>
                                        prop.name === "Position_Longitude"
                                    )?.value;
                                    return lat && lng
                                      ? `${parseFloat(lat).toFixed(
                                          4
                                        )}, ${parseFloat(lng).toFixed(4)}`
                                      : "N/A";
                                  })()
                                : "N/A"}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 8,
                          padding: "16px",
                        }}
                      >
                        <Typography
                          variant="subtitle1"
                          style={{
                            marginBottom: "12px",
                            fontWeight: 600,
                            color: "#1a237e",
                          }}
                        >
                          Technical Specifications
                        </Typography>
                        <Box display="flex" flexDirection="column" gap={1.5}>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Power Output:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              2500 HP
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Flow Rate:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              1200 GPM
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Pressure Rating:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              15,000 PSI
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Operating Temperature:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              -40°F to 200°F
                            </Typography>
                          </Box>
                          <Box
                            display="flex"
                            justifyContent="space-between"
                            alignItems="center"
                          >
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 500, color: "#666" }}
                            >
                              Material:
                            </Typography>
                            <Typography
                              variant="caption"
                              style={{ fontWeight: 600, color: "#1a237e" }}
                            >
                              Stainless Steel
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Box
                        style={{
                          border: "1px solid #e0e0e0",
                          borderRadius: 8,
                          padding: "16px",
                        }}
                      >
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          style={{ marginBottom: "10px" }}
                        >
                          <Typography
                            variant="subtitle1"
                            style={{ fontWeight: 600, color: "#1a237e" }}
                          >
                            Asset Performance
                          </Typography>
                          <IconButton
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleShowChartsPanel();
                            }}
                            size="small"
                            style={{
                              backgroundColor: "#f5f5f5",
                              borderRadius: "8px",
                              padding: "8px",
                              "&:hover": {
                                backgroundColor: "#e3f2fd",
                                transform: "scale(1.05)",
                              },
                              transition: "all 0.2s ease",
                            }}
                            title="View Individual Charts"
                          >
                            <BarChartIcon
                              style={{ fontSize: "20px", color: "#1a237e" }}
                            />
                          </IconButton>
                        </Box>
                        <div
                          className="map-container"
                          style={{ position: "relative" }}
                        >
                          <CustomLegend
                            channels={assetWithChartData.channels}
                            visibleSeries={
                              Object.keys(visibleSeries).length ===
                              assetWithChartData.channels.length
                                ? visibleSeries
                                : assetWithChartData.channels.reduce(
                                    (acc, channel) => {
                                      acc[channel.id] = true;
                                      return acc;
                                    },
                                    {}
                                  )
                            }
                            onLegendClick={handleLegendClick}
                            assetType={assetWithChartData.type}
                            chartColors={(() => {
                              const colorSchemes = {
                                pump: [
                                  "#ff9800",
                                  "#f44336",
                                  "#8bc34a",
                                  "#cddc39",
                                ],
                                generator: [
                                  "#4caf50",
                                  "#2196f3",
                                  "#e91e63",
                                  "#f44336",
                                ],
                                compressor: [
                                  "#9c27b0",
                                  "#607d8b",
                                  "#e91e63",
                                  "#f44336",
                                ],
                                heat_exchanger: [
                                  "#e91e63",
                                  "#ff5722",
                                  "#8bc34a",
                                  "#f44336",
                                ],
                                motor: [
                                  "#00bcd4",
                                  "#8bc34a",
                                  "#e91e63",
                                  "#f44336",
                                ],
                                fan: [
                                  "#795548",
                                  "#9e9e9e",
                                  "#e91e63",
                                  "#f44336",
                                ],
                                transformer: [
                                  "#2196f3",
                                  "#ff9800",
                                  "#8bc34a",
                                  "#4caf50",
                                ],
                                boiler: [
                                  "#3f51b5",
                                  "#673ab7",
                                  "#8bc34a",
                                  "#f44336",
                                ],
                                conveyor: [
                                  "#ffc107",
                                  "#cddc39",
                                  "#e91e63",
                                  "#f44336",
                                ],
                                reactor: [
                                  "#ff5722",
                                  "#4caf50",
                                  "#f44336",
                                  "#8bc34a",
                                ],
                                cooling_tower: [
                                  "#00bcd4",
                                  "#009688",
                                  "#8bc34a",
                                  "#f44336",
                                ],
                                turbine: [
                                  "#f44336",
                                  "#e91e63",
                                  "#8bc34a",
                                  "#f44336",
                                ],
                              };
                              const colors = colorSchemes[
                                assetWithChartData.type
                              ] || ["#4caf50", "#2196f3", "#e91e63", "#f44336"];
                              return assetWithChartData.channels.map(
                                (channel, index) =>
                                  colors[index % colors.length]
                              );
                            })()}
                          />
                          <div
                            style={{
                              height: "320px",
                              width: "calc(100% + 20px)",
                              marginLeft: "-10px",
                              marginRight: "-16px",
                              background: "#fff",
                              boxSizing: "border-box",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {assetWithChartData.channels.length > 0 && (
                              <div className="main-chart-container">
                                <CustomChart
                                  title="Asset Performance"
                                  data={assetWithChartData.channels
                                    .filter(
                                      (channel) =>
                                        visibleSeries[channel.id] !== false
                                    )
                                    .map((channel, index) => {
                                      const channelData = assetWithChartData.chartData[channel.id] || [];
                                      const sortedData = channelData.sort((a, b) => a[0] - b[0]);
                                      const filteredData = sortedData.filter(
                                        (_, idx, array) =>
                                          idx === 0 ||
                                          array[idx][0] !== array[idx - 1][0]
                                      );
                                      
                                      return {
                                        color: (() => {
                                          const colorSchemes = {
                                            pump: [
                                              "#ff9800",
                                              "#f44336",
                                              "#8bc34a",
                                              "#cddc39",
                                            ],
                                            generator: [
                                              "#4caf50",
                                              "#2196f3",
                                              "#e91e63",
                                              "#f44336",
                                            ],
                                            compressor: [
                                              "#9c27b0",
                                              "#607d8b",
                                              "#e91e63",
                                              "#f44336",
                                            ],
                                            heat_exchanger: [
                                              "#e91e63",
                                              "#ff5722",
                                              "#8bc34a",
                                              "#f44336",
                                            ],
                                            motor: [
                                              "#00bcd4",
                                              "#8bc34a",
                                              "#e91e63",
                                              "#f44336",
                                            ],
                                            fan: [
                                              "#795548",
                                              "#9e9e9e",
                                              "#e91e63",
                                              "#f44336",
                                            ],
                                            transformer: [
                                              "#2196f3",
                                              "#ff9800",
                                              "#8bc34a",
                                              "#4caf50",
                                            ],
                                            boiler: [
                                              "#3f51b5",
                                              "#673ab7",
                                              "#8bc34a",
                                              "#f44336",
                                            ],
                                            conveyor: [
                                              "#ffc107",
                                              "#cddc39",
                                              "#e91e63",
                                              "#f44336",
                                            ],
                                            reactor: [
                                              "#ff5722",
                                              "#4caf50",
                                              "#f44336",
                                              "#8bc34a",
                                            ],
                                            cooling_tower: [
                                              "#00bcd4",
                                              "#009688",
                                              "#8bc34a",
                                              "#f44336",
                                            ],
                                            turbine: [
                                              "#f44336",
                                              "#e91e63",
                                              "#8bc34a",
                                              "#f44336",
                                            ],
                                          };
                                          const colors = colorSchemes[
                                            assetWithChartData.type
                                          ] || [
                                            "#4caf50",
                                            "#2196f3",
                                            "#e91e63",
                                            "#f44336",
                                          ];

                                          const originalIndex =
                                            assetWithChartData.channels.findIndex(
                                              (c) => c.id === channel.id
                                            );
                                          return colors[
                                            originalIndex % colors.length
                                          ];
                                        })(),
                                        points: filteredData,
                                      };
                                    })}
                                  width={372}
                                  height={250}
                                  showGrid={true}
                                  showPoints={true}
                                  lineWidth={2}
                                  multiLine={true}
                                  onPointClick={handlePointClick}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </Box>
                    </Grid>
                  </Grid>
                </div>
              )}
            </DialogContent>
          </div>
        </>
      )}

      <ChartsPanel
        channels={assetWithChartData?.channels}
        chartData={assetWithChartData?.chartData}
        assetType={assetWithChartData?.type}
        onClose={() => {
          // Якщо зараз вибрано dataTables, встановлюємо charts перед закриттям
          if (filterType === "dataTables") {
            setFilterType("charts");
          }
          setShowChartsPanel(false);
        }}
        filterType={filterType}
        setFilterType={setFilterType}
        isVisible={showChartsPanel}
        apiData={apiData}
        isTableDataLoading={isTableDataLoading}
        onLoadTableData={loadMetricsList}
        hasLoadedTableData={hasLoadedTableData}
        asset={asset}
        tablePagination={tablePagination}
        tableSorting={tableSorting}
        onSortChange={handleSortChange}
        onLoadMoreTableData={loadMoreTableData}
        metricsList={metricsList}
        metricsData={metricsData}
        metricsPagination={metricsPagination}
        metricsLoading={metricsLoading}
        isMetricsListLoading={isMetricsListLoading}
        onMetricPaginationChange={handleMetricPaginationChange}
        setMetricsPagination={setMetricsPagination}
        onPointClick={handlePointClick}
      />
    </>
  );
};

export default AssetDetailModal;
